import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AnalyticsEvent, track } from '@bonusly/analytics';
import { useHttpClient } from '@bonusly/http-client';
import { useCurrentUser, useIdentity } from '@bonusly/who-am-i';

import { QueryKey as RecognitionBonusQueryKey } from '../../../components/recognition/bonus/hooks/types';
import { BizyCompleteEvent, BizyNodeAppendedEvent, BizyNodeTokenEvent, useBizyChannel } from '../../../hooks/use-bizy-channel';
import { executePageAction, useActionSchemaRegistry } from '../../../lib/bizy-actions';
import { QueryKey as RecognitionFeedQueryKey } from '../../../modules/bonus-feed/features/recognition/api/hooks/types';
import { QueryKey as GrowthQueryKey } from '../../../modules/growth/types/growth-query-key';
import { useBizyCancelMutation, useBizyChatV2Mutation } from '../../../queries/bizy';
import { bizyConversationPath, useBizyUserConversation } from '../../../queries/bizy-conversations';
import { invalidateCelebrationsAdminCaches } from '../../../queries/celebrations';
import { BizyAssistantMessageNode, BizyUserConversationDetail } from '../../../schemas/bizy-conversations';
import type { BizyState } from '../../bizy';
import type { NavigateFn } from '../bizy-navigation-context';
import { clearBizySession, loadBizySession, saveBizySession } from '../lib/session-storage';
import { useBizyWidgetStore } from '../store/use-bizy-widget-store';
import { useBizyReportContext } from './use-bizy-report-context';

export type FeedbackType = 'positive' | 'negative' | undefined;
export type ApprovalDecision = 'approved' | 'denied';

/** Card-shape payload passed to BizyCardRenderer. The renderer reads
 *  `card.choice` to decide whether to show post-click visual state
 *  (e.g. "Sent ✓" on an approval card after the user clicked). */
export interface CardNode {
	type: string;
	payload: Record<string, unknown>;
	/** Set after the user acts on a decision card. */
	choice?: ApprovalDecision;
}

export interface ChatMessage {
	role: 'user' | 'assistant';
	content: string;
	id?: string;
	feedbackType?: FeedbackType;
	key?: string;
	toolsUsed?: string[];
	timestamp?: Date;
	/** Typed AssistantMessage document — ordered sequence of nodes
	 *  (markdown / approval_card / content_card / draft_card). Populated
	 *  on every message after the conversation API surfacing landed.
	 *  Empty for any historical row that pre-dates the backfill — those
	 *  fall back to rendering `content` as plain markdown. */
	assistantMessage?: BizyAssistantMessageNode[];
	/** Per-card user choices, indexed by the card's position among the
	 *  interactive (card-typed) nodes in `assistantMessage`. Lets
	 *  BizyDocumentRenderer show post-click state (e.g., "Sent ✓")
	 *  after a reload, since these ride the message in sessionStorage. */
	nodeChoices?: Record<number, ApprovalDecision>;
}

interface UseBizyGlobalChatOptions {
	extraContext?: Record<string, string>;
	/** Override React Router navigate for non-SPA contexts (e.g. MFE on Rails pages). */
	navigateFn?: NavigateFn;
}

const ROLE_USER = 'user' as const;
const ROLE_ASSISTANT = 'assistant' as const;

const EMOTION_CURIOUS: BizyState = 'curious';
const EMOTION_DISAPPOINTED: BizyState = 'disappointed';

const STREAM_TIMEOUT_MS = 300_000;

let messageKeyCounter = 0;
function nextMessageKey(): string {
	return `msg-${Date.now()}-${messageKeyCounter++}`;
}

let pendingIdCounter = 0;
function nextPendingRequestId(): string {
	return `pending-${Date.now()}-${pendingIdCounter++}`;
}

interface BizyMessageLike {
	userMessage: string | null;
	bizyResponse: string;
	id?: string;
	feedbackType?: FeedbackType | null;
	createdAt?: Date;
	// Accepts null because the Zod schema infers `… | null | undefined`
	// (`z.array(BizyAssistantMessageNode).nullable().optional()`). The
	// body normalizes empty / null / missing to `undefined` so callers
	// don't have to special-case.
	assistantMessage?: BizyAssistantMessageNode[] | null;
}

// Hydrates a stored Bizy message into ChatMessage turns. Skips the user
// bubble for proactive (assistant-first) records where userMessage is null —
// otherwise we'd render an empty user bubble. Used by all three hydration
// paths (refetch, resume, dropdown-load) to keep them in sync.
function hydrateBizyMessage(msg: BizyMessageLike): ChatMessage[] {
	const assistantMessage = msg.assistantMessage?.length ? msg.assistantMessage : undefined;
	const turns: ChatMessage[] = [];
	if (msg.userMessage) {
		turns.push({ role: ROLE_USER, content: msg.userMessage, key: nextMessageKey(), timestamp: msg.createdAt });
	}
	turns.push({
		role: ROLE_ASSISTANT,
		content: msg.bizyResponse,
		id: msg.id,
		feedbackType: msg.feedbackType ?? undefined,
		key: nextMessageKey(),
		assistantMessage,
	});
	return turns;
}

/**
 * Hook for managing Bizy streaming chat state in admin pages.
 *
 * Features:
 * - request_id filtering with cross-tab sync
 * - 5m streaming timeout with retry
 * - Connection health awareness
 * - Cross-tab message sync via onOtherTabComplete
 * - Refetch conversation on tab focus
 * - Smart 409 handling (disabled input instead of error)
 */
export function useBizyGlobalChat(options: UseBizyGlobalChatOptions = {}) {
	const { extraContext, navigateFn } = options;
	const sessionData = loadBizySession();

	const [messages, setMessages] = useState<ChatMessage[]>(sessionData?.messages || []);
	const [conversationId, setConversationId] = useState<string | undefined>(sessionData?.conversationId);
	const [bizyEmotion, setBizyEmotion] = useState<BizyState>(sessionData?.bizyEmotion || EMOTION_CURIOUS);
	const messagesRef = useRef(messages);
	const bizyEmotionRef = useRef(bizyEmotion);
	messagesRef.current = messages;
	bizyEmotionRef.current = bizyEmotion;
	const [loadConversationId, setLoadConversationId] = useState<string | undefined>();
	const [isLoading, setIsLoading] = useState(false);
	const [isTimedOut, setIsTimedOut] = useState(false);
	const [isOtherTabLoading, setIsOtherTabLoading] = useState(false);
	const [activeToolCalls, setActiveToolCalls] = useState<string[]>([]);

	const { data: loadedConversation, isError: isLoadError } = useBizyUserConversation(loadConversationId);

	const currentUser = useCurrentUser();
	const http = useHttpClient();
	const queryClient = useQueryClient();
	const { mutate } = useBizyChatV2Mutation();
	const { mutate: cancelMutate } = useBizyCancelMutation();
	const { t } = useTranslation('admin_bizy');
	const reportContext = useBizyReportContext((state) => state.context);
	const resumeConversationId = useBizyWidgetStore((state) => state.resumeConversationId);
	const resumeMessages = useBizyWidgetStore((state) => state.resumeMessages);
	const clearResume = useBizyWidgetStore((state) => state.clearResume);
	const { refetchProfile } = useIdentity();
	const routerNavigate = useNavigate();
	const navigate = navigateFn ?? routerNavigate;
	const { getSchema } = useActionSchemaRegistry();

	const isLoadingRef = useRef(false);
	const activeRequestIdRef = useRef<string | undefined>();
	const streamingContentRef = useRef('');
	// Typed-document accumulator. `node_token` events append to (or
	// create) the markdown node identified by `node_id`; `node_appended`
	// events push a complete card or one-shot markdown node. The map
	// keys events by `node_id` so chunks for the same node fold together
	// regardless of arrival order.
	const documentNodesRef = useRef<BizyAssistantMessageNode[]>([]);
	const documentIndexByIdRef = useRef<Map<number, number>>(new Map());
	// Per-node seq buffer: node_id → (seq → token content). Tokens for the
	// same markdown node are stored by their backend-assigned monotonic seq,
	// then joined in seq order to derive payload.content. Lets ActionCable
	// deliver events out of order (transient reconnects, websocket buffer
	// flushes, proxy reordering) without garbling the rendered text.
	const tokensByNodeIdRef = useRef<Map<number, Map<number, string>>>(new Map());
	// Single source of truth for clearing per-turn streaming
	// accumulators. Every state-reset path calls this so nodes from an
	// interrupted turn can't leak into the next one's assistantMessage.
	const resetStreamingAccumulators = () => {
		documentNodesRef.current = [];
		documentIndexByIdRef.current = new Map();
		tokensByNodeIdRef.current = new Map();
	};
	const messageSentAtRef = useRef<number | null>(null);
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
	const lastUserMessageRef = useRef<string>('');
	const requestGenerationRef = useRef(0);
	const conversationIdRef = useRef<string | undefined>();
	conversationIdRef.current = conversationId;

	const skipNextPersistRef = useRef(false);

	const refreshRecognitionSurfaces = useCallback(() => {
		queryClient.invalidateQueries([RecognitionFeedQueryKey.BonusesInfinite]);
		queryClient.invalidateQueries([RecognitionFeedQueryKey.Bonus]);
		queryClient.invalidateQueries([RecognitionFeedQueryKey.UserProfile]);
		queryClient.invalidateQueries([RecognitionBonusQueryKey.ProfileBonuses]);
		void refetchProfile();
	}, [queryClient, refetchProfile]);

	// --- Timeout management ---
	const clearStreamTimeout = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = undefined;
		}
	}, []);

	const handleTimeout = useCallback((reason: 'stream_timeout' | 'connection_lost' = 'stream_timeout') => {
		if (!isLoadingRef.current) return;

		track(AnalyticsEvent.Bizy.ResponseReceived, {
			success: false,
			errorType: reason,
			path: window.location.pathname,
		});

		setIsTimedOut(true);
		setIsLoading(false);
		isLoadingRef.current = false;
		setActiveToolCalls([]);
		// Maintain the pending-ref hygiene invariant: every state-reset path
		// clears this ref so nodes from an interrupted request can't leak
		// into the next attempt.
		resetStreamingAccumulators();
		messageSentAtRef.current = null;
	}, []);

	const resetStreamTimeout = useCallback(() => {
		clearStreamTimeout();
		timeoutRef.current = setTimeout(handleTimeout, STREAM_TIMEOUT_MS);
	}, [clearStreamTimeout, handleTimeout]);

	useEffect(() => clearStreamTimeout, [clearStreamTimeout]);

	// --- Refetch conversation from backend (for cross-tab sync & tab focus) ---
	const refetchConversation = useCallback(
		async (convId: string) => {
			const generation = requestGenerationRef.current;
			try {
				const res = await http.get(bizyConversationPath(convId), {
					headers: { 'Key-Inflection': 'camel' },
				});

				if (requestGenerationRef.current !== generation) return;

				// Only apply if this is still the active conversation (avoids overwriting after dropdown switch).
				if (conversationIdRef.current !== convId) return;

				const parsed = BizyUserConversationDetail.safeParse(res.data.conversation);
				if (!parsed.success) return;

				const hydrated: ChatMessage[] = parsed.data.messages.flatMap(hydrateBizyMessage);
				setMessages((prev) => {
					// Carry forward per-card choices the user already made,
					// keyed by message id so refetches don't reset the
					// post-click visual state.
					const choicesByMessageId = new Map<string, Record<number, ApprovalDecision>>();
					for (const msg of prev) {
						if (msg.id && msg.nodeChoices && Object.keys(msg.nodeChoices).length > 0) {
							choicesByMessageId.set(msg.id, msg.nodeChoices);
						}
					}
					if (choicesByMessageId.size === 0) return hydrated;
					return hydrated.map((msg) => {
						if (!msg.id) return msg;
						const choices = choicesByMessageId.get(msg.id);
						return choices ? { ...msg, nodeChoices: choices } : msg;
					});
				});
			} catch {
				// Silently fail — the in-memory state is still usable
			}
		},
		[http]
	);

	// --- Tab focus: refetch conversation to catch up on other tabs' messages ---
	// Skip when loading a different conversation from dropdown so we don't overwrite with the previous one.
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (loadConversationId) return;
			if (document.visibilityState === 'visible' && conversationIdRef.current && !isLoadingRef.current) {
				refetchConversation(conversationIdRef.current);
				setIsOtherTabLoading(false);
			}
		};
		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
	}, [refetchConversation, loadConversationId]);

	// Rehydrate conversation from history page or dropdown
	useEffect(() => {
		if (resumeConversationId && resumeMessages) {
			clearStreamTimeout();
			activeRequestIdRef.current = undefined;
			requestGenerationRef.current += 1;

			const hydrated: ChatMessage[] = resumeMessages.flatMap(hydrateBizyMessage);
			setMessages(hydrated);
			setConversationId(resumeConversationId);
			setBizyEmotion(EMOTION_CURIOUS);
			setIsLoading(false);
			isLoadingRef.current = false;
			setIsTimedOut(false);
			setIsOtherTabLoading(false);
			setActiveToolCalls([]);
			streamingContentRef.current = '';
			clearResume();

			// Persist immediately; skip the generic persist effect this cycle so it doesn't overwrite with stale state.
			skipNextPersistRef.current = true;
			saveBizySession({ messages: hydrated, conversationId: resumeConversationId, bizyEmotion: EMOTION_CURIOUS });
		}
	}, [resumeConversationId, resumeMessages, clearResume, clearStreamTimeout]);

	// Dropdown: when user selects a conversation, we fetch and apply it here. One effect, no store.
	useEffect(() => {
		if (!loadConversationId || !loadedConversation || loadedConversation.conversationId !== loadConversationId) return;

		clearStreamTimeout();
		activeRequestIdRef.current = undefined;
		requestGenerationRef.current += 1;

		const hydrated: ChatMessage[] = loadedConversation.messages.flatMap(hydrateBizyMessage);
		setMessages(hydrated);
		setConversationId(loadConversationId);
		setBizyEmotion(EMOTION_CURIOUS);
		setIsLoading(false);
		isLoadingRef.current = false;
		setIsTimedOut(false);
		setIsOtherTabLoading(false);
		setActiveToolCalls([]);
		streamingContentRef.current = '';
		setLoadConversationId(undefined);

		skipNextPersistRef.current = true;
		saveBizySession({ messages: hydrated, conversationId: loadConversationId, bizyEmotion: EMOTION_CURIOUS });
	}, [loadConversationId, loadedConversation, clearStreamTimeout]);

	// Clear loading state if the conversation fetch failed (e.g. network error)
	useEffect(() => {
		if (loadConversationId && isLoadError) setLoadConversationId(undefined);
	}, [loadConversationId, isLoadError]);

	// --- BizyChannel event handlers (own tab) ---
	const handleToken = useCallback(
		(content: string) => {
			if (!isLoadingRef.current) return;

			resetStreamTimeout();
			// Token events carry the full accumulated response (not a delta). Replace when
			// the incoming content is longer; ignore shorter out-of-order arrivals.
			if (content.length >= streamingContentRef.current.length) {
				streamingContentRef.current = content;
			}
			const updatedContent = streamingContentRef.current;

			setMessages((prev) => {
				const lastMessage = prev[prev.length - 1];
				if (lastMessage?.role === ROLE_ASSISTANT && !lastMessage.id) {
					return [...prev.slice(0, -1), { ...lastMessage, content: updatedContent }];
				}
				return [...prev, { role: ROLE_ASSISTANT, content: updatedContent, key: nextMessageKey() }];
			});
		},
		[resetStreamTimeout]
	);

	const handleComplete = useCallback(
		(data: BizyCompleteEvent['data']) => {
			// Connector response: replace the last assistant bubble with the full accumulated content
			if (data.connector_response && data.content) {
				setMessages((prev) => {
					const lastMessage = prev[prev.length - 1];
					if (lastMessage?.role === ROLE_ASSISTANT && lastMessage.id === data.message_id) {
						return [...prev.slice(0, -1), { ...lastMessage, content: data.content }];
					}
					return [...prev, { role: ROLE_ASSISTANT, content: data.content, id: data.message_id || undefined, key: nextMessageKey() }];
				});
				setIsTimedOut(false);
				// Connector responses don't carry typed nodes; drop any that
				// accumulated during the connector_processing phase against the
				// stripped conductor text.
				resetStreamingAccumulators();
				if (isLoadingRef.current) {
					clearStreamTimeout();
					setIsLoading(false);
					isLoadingRef.current = false;
					setIsOtherTabLoading(false);
					setActiveToolCalls([]);
					streamingContentRef.current = '';
					messageSentAtRef.current = null;
				}
				return;
			}

			if (!isLoadingRef.current) return;

			clearStreamTimeout();

			setConversationId(data.conversation_id);

			// connector_processing: conductor is done but a connector is still working.
			// Strip the conductor's streamed text and keep thinking. Skip analytics —
			// the user hasn't received a visible response yet.
			if (data.connector_processing) {
				setMessages((prev) => {
					const lastMessage = prev[prev.length - 1];
					if (lastMessage?.role === ROLE_ASSISTANT && !lastMessage.id) {
						return prev.slice(0, -1);
					}
					return prev;
				});
				setActiveToolCalls([]);
				streamingContentRef.current = '';
				// Any nodes accumulated against the discarded conductor text
				// have stale offsets — drop them so they don't get merged onto
				// the connector's eventual response.
				resetStreamingAccumulators();
				resetStreamTimeout();
				return;
			}

			const ttftMs = messageSentAtRef.current && data.first_token_ms ? data.first_token_ms : undefined;

			track(AnalyticsEvent.Bizy.ResponseReceived, {
				responseTimeMs: data.response_time_ms,
				emotion: data.emotion || EMOTION_CURIOUS,
				success: true,
				toolsUsed: data.tools_used || [],
				ttftMs,
				path: window.location.pathname,
			});

			const toolsUsed = data.tools_used?.length ? data.tools_used : undefined;
			// On completion the server has the authoritative, post-processed
			// response text. Drop live markdown nodes (working plans + raw
			// streamed answer text) and rebuild from `data.content`, while
			// preserving card nodes emitted during the turn.
			const completedNodes = [
				...(data.content.trim() ? [{ type: 'markdown' as const, payload: { content: data.content } }] : []),
				...documentNodesRef.current.filter((node) => node.type !== 'markdown'),
			];
			const assistantMessage = completedNodes.length > 0 ? completedNodes : undefined;
			resetStreamingAccumulators();

			setMessages((prev) => {
				const lastMessage = prev[prev.length - 1];
				if (lastMessage?.role === ROLE_ASSISTANT) {
					return [...prev.slice(0, -1), { ...lastMessage, content: data.content, id: data.message_id || undefined, toolsUsed, assistantMessage }];
				}
				return [
					...prev,
					{
						role: ROLE_ASSISTANT,
						content: data.content,
						id: data.message_id || undefined,
						key: nextMessageKey(),
						toolsUsed,
						assistantMessage,
					},
				];
			});

			setBizyEmotion((data.emotion as BizyState) || EMOTION_CURIOUS);
			setIsLoading(false);
			isLoadingRef.current = false;
			setIsOtherTabLoading(false);
			setActiveToolCalls([]);
			streamingContentRef.current = '';
			messageSentAtRef.current = null;
		},
		[clearStreamTimeout, resetStreamTimeout]
	);

	const handleError = useCallback(
		(message: string) => {
			if (!isLoadingRef.current) return;

			clearStreamTimeout();
			resetStreamingAccumulators();

			track(AnalyticsEvent.Bizy.ResponseReceived, {
				success: false,
				errorType: 'streaming_error',
				path: window.location.pathname,
			});

			setMessages((prev) => {
				const lastMessage = prev[prev.length - 1];
				if (lastMessage?.role === ROLE_ASSISTANT && !lastMessage.id) {
					return [...prev.slice(0, -1), { ...lastMessage, content: message }];
				}
				return [...prev, { role: ROLE_ASSISTANT, content: message, key: nextMessageKey() }];
			});

			setBizyEmotion(EMOTION_DISAPPOINTED);
			setIsLoading(false);
			isLoadingRef.current = false;
			setIsOtherTabLoading(false);
			setActiveToolCalls([]);
			streamingContentRef.current = '';
			messageSentAtRef.current = null;
		},
		[clearStreamTimeout]
	);

	const handleCancelled = useCallback(
		(_content: string) => {
			if (!isLoadingRef.current) return;

			clearStreamTimeout();
			resetStreamingAccumulators();

			setMessages((prev) => {
				const last = prev[prev.length - 1];
				if (!last) return prev;
				if (last.role === ROLE_ASSISTANT && !last.id) {
					const secondLast = prev[prev.length - 2];
					if (secondLast?.role === ROLE_USER) return prev.slice(0, -2);
					return prev.slice(0, -1);
				}
				if (last.role === ROLE_USER) return prev.slice(0, -1);
				return prev;
			});

			setIsLoading(false);
			isLoadingRef.current = false;
			setIsOtherTabLoading(false);
			setActiveToolCalls([]);
			streamingContentRef.current = '';
			messageSentAtRef.current = null;
		},
		[clearStreamTimeout]
	);

	const handleToolCall = useCallback(
		(data: { tool_name: string }) => {
			if (!isLoadingRef.current) return;
			resetStreamTimeout();
			setActiveToolCalls((prev) => [...prev, data.tool_name]);
		},
		[resetStreamTimeout]
	);

	// Typed AssistantMessage event handlers. Build the document
	// incrementally as nodes arrive; the message's assistantMessage
	// snapshot reflects the in-progress state so the renderer can
	// update on each event.
	const handleNodeToken = useCallback(
		(data: BizyNodeTokenEvent['data']) => {
			if (!isLoadingRef.current) return;
			resetStreamTimeout();

			const { content, node_id, seq } = data;
			if (content === '') return;

			// Guard against node_id collisions with non-markdown nodes
			// already registered by handleNodeAppended (cards). Silently
			// ignore the token rather than overwriting the card with a
			// bare markdown payload — matches the original reducer's
			// behavior before the seq-buffer rewrite.
			const idx = documentIndexByIdRef.current.get(node_id);
			if (idx !== undefined && documentNodesRef.current[idx]?.type !== 'markdown') return;

			// Buffer the token by its backend-assigned seq, then re-derive
			// payload.content as the seq-ordered concatenation. This is what
			// lets out-of-order ActionCable delivery resolve to coherent text:
			// the late-arriving fragment slots into its correct position
			// instead of being welded onto the tail.
			let nodeTokens = tokensByNodeIdRef.current.get(node_id);
			if (!nodeTokens) {
				nodeTokens = new Map();
				tokensByNodeIdRef.current.set(node_id, nodeTokens);
			}
			nodeTokens.set(seq, content);
			const orderedContent = [...nodeTokens.entries()]
				.sort(([a], [b]) => a - b)
				.map(([, c]) => c)
				.join('');

			if (idx !== undefined) {
				documentNodesRef.current[idx] = { type: 'markdown', payload: { content: orderedContent } };
			} else {
				documentNodesRef.current.push({ type: 'markdown', payload: { content: orderedContent } });
				documentIndexByIdRef.current.set(node_id, documentNodesRef.current.length - 1);
			}

			// Reflect the in-progress document on the open assistant message so
			// the BizyDocumentRenderer (when active) can re-render incrementally.
			const snapshot = [...documentNodesRef.current];
			setMessages((prev) => {
				const lastMessage = prev[prev.length - 1];
				if (lastMessage?.role === ROLE_ASSISTANT && !lastMessage.id) {
					return [...prev.slice(0, -1), { ...lastMessage, assistantMessage: snapshot }];
				}
				// First node event with no open assistant bubble — create one. Mirrors
				// handleToken's "create on first token" behavior.
				return [...prev, { role: ROLE_ASSISTANT, content: '', key: nextMessageKey(), assistantMessage: snapshot }];
			});
		},
		[resetStreamTimeout]
	);

	const handleNodeAppended = useCallback(
		(data: BizyNodeAppendedEvent['data']) => {
			// On live turns, deferred cards flush from `NodeStreamer#finish_turn`
			// on the backend AFTER the LLM's streamed tokens complete but BEFORE
			// stream_complete. In practice cable events should hit the client in
			// order (tokens → node_appended → complete), but cards were going
			// missing on the first live turn (they appeared only after a reload
			// from persisted state). The most likely cause is a scheduler race
			// where `handleComplete` flips `isLoadingRef` before the
			// `node_appended` event is drained from the cable queue. Appending
			// a late card to the last assistant message is always correct —
			// cross-tab events are filtered out upstream in `useBizyChannel`'s
			// `received` handler, so by the time we're here the event belongs
			// to this tab's session.
			resetStreamTimeout();

			const { type, payload, node_id } = data;
			const parsedNode = BizyAssistantMessageNode.safeParse(
				type === 'markdown'
					? { type: 'markdown', payload: { ...(payload ?? {}), content: (payload?.content as string | undefined) ?? '' } }
					: { type, payload: payload ?? {} }
			);
			if (!parsedNode.success) return;
			const node = parsedNode.data;

			// `handleComplete` resets documentNodesRef to [] when it bakes the
			// final assistantMessage. A late card would land as a single-element
			// snapshot that wipes the baked document. Rehydrate the ref from
			// the last message's assistantMessage in that case so the late
			// card is merged rather than replacing.
			if (!isLoadingRef.current && documentNodesRef.current.length === 0) {
				const lastMessage = messagesRef.current[messagesRef.current.length - 1];
				if (lastMessage?.role === ROLE_ASSISTANT && lastMessage.assistantMessage?.length) {
					documentNodesRef.current = [...lastMessage.assistantMessage];
				}
			}
			documentNodesRef.current.push(node);
			documentIndexByIdRef.current.set(node_id, documentNodesRef.current.length - 1);

			const snapshot = [...documentNodesRef.current];
			setMessages((prev) => {
				const lastMessage = prev[prev.length - 1];
				if (lastMessage?.role === ROLE_ASSISTANT) {
					// Append to the last assistant message whether it's still open
					// (no id) OR has just been finalized by `handleComplete`.
					// A late card from the just-completed turn goes onto the
					// message it belongs to, not into a new bubble.
					return [...prev.slice(0, -1), { ...lastMessage, assistantMessage: snapshot }];
				}
				return [...prev, { role: ROLE_ASSISTANT, content: '', key: nextMessageKey(), assistantMessage: snapshot }];
			});
		},
		[resetStreamTimeout]
	);

	const handlePageAction = useCallback(
		async (data: any) => {
			if (!isLoadingRef.current) return;
			resetStreamTimeout();

			try {
				if (data.action === 'navigate_to_page') {
					if (data.page_path) {
						const path = String(data.page_path);
						if (path.startsWith('/') && !path.startsWith('//')) {
							saveBizySession({
								messages: messagesRef.current,
								conversationId: conversationIdRef.current,
								bizyEmotion: bizyEmotionRef.current,
							});
							navigate(path);
							requestAnimationFrame(() => {
								const el = document.querySelector('[data-bizy-chat-scroll]');
								if (el) el.scrollTop = el.scrollHeight;
							});
						}
					}
					return;
				}

				if (data.action === 'invalidate_celebration_queries') {
					invalidateCelebrationsAdminCaches(queryClient);
					return;
				}

				if (data.action === 'invalidate_recognition_queries') {
					refreshRecognitionSurfaces();
					return;
				}

				if (data.action === 'invalidate_meetings_queries') {
					queryClient.invalidateQueries({ queryKey: [GrowthQueryKey.MeetingsV2] });
					return;
				}

				const pageId = data.report_type;
				const schema = getSchema(pageId);
				await executePageAction(data, schema, navigate);
			} catch (error) {
				console.error('[Bizy] Failed to execute action:', error);
			}
		},
		[getSchema, navigate, queryClient, refreshRecognitionSurfaces, resetStreamTimeout]
	);

	const handleDisconnected = useCallback(() => {
		if (isLoadingRef.current) {
			handleTimeout('connection_lost');
		}
	}, [handleTimeout]);

	// --- Cross-tab event handlers ---
	const handleOtherTabComplete = useCallback(
		(data: BizyCompleteEvent['data']) => {
			if (data.connector_processing) return;
			setIsOtherTabLoading(false);
			if (data.conversation_id && conversationIdRef.current === data.conversation_id && !isLoadingRef.current) {
				refetchConversation(data.conversation_id);
			}
		},
		[refetchConversation]
	);

	const handleOtherTabError = useCallback(() => {
		setIsOtherTabLoading(false);
	}, []);

	const handleOtherTabLoading = useCallback(() => {
		setIsOtherTabLoading(true);
	}, []);

	const handleSupportEscalated = useCallback(() => {
		if (isLoadingRef.current) {
			clearStreamTimeout();
			setMessages((prev) => {
				const lastMessage = prev[prev.length - 1];
				if (lastMessage?.role === ROLE_ASSISTANT && !lastMessage.id) {
					return prev.slice(0, -1);
				}
				return prev;
			});
			setIsLoading(false);
			isLoadingRef.current = false;
			setIsOtherTabLoading(false);
			setActiveToolCalls([]);
			streamingContentRef.current = '';
			resetStreamingAccumulators();
			messageSentAtRef.current = null;
		}
	}, [clearStreamTimeout]);

	// Subscribe to BizyChannel
	useBizyChannel(currentUser?.id, {
		onToken: handleToken,
		onNodeToken: handleNodeToken,
		onNodeAppended: handleNodeAppended,
		onComplete: handleComplete,
		onError: handleError,
		onToolCall: handleToolCall,
		onPageAction: handlePageAction,
		onCancelled: handleCancelled,
		onPreliminaryToken: resetStreamTimeout,
		onDisconnected: handleDisconnected,
		onOtherTabComplete: handleOtherTabComplete,
		onOtherTabError: handleOtherTabError,
		onSupportEscalated: handleSupportEscalated,
		onOtherTabLoading: handleOtherTabLoading,
		activeRequestIdRef,
	});

	useEffect(() => clearStreamTimeout, [clearStreamTimeout]);

	// Persist state to sessionStorage whenever it changes (skip once after resume so we don't overwrite with stale state).
	useEffect(() => {
		if (skipNextPersistRef.current) {
			skipNextPersistRef.current = false;
			return;
		}
		saveBizySession({ messages, conversationId, bizyEmotion });
	}, [messages, conversationId, bizyEmotion]);

	// On mount, when sessionStorage has a conversation, refetch from the API
	// to validate the cached state. Two reasons:
	//   1. The last assistant message may be partial — the complete event was
	//      never received (killed worker, page refresh mid-stream, etc.) — so
	//      we trust the API copy and drop the partial tail.
	//   2. The cached message content may have been corrupted by out-of-order
	//      node_token streaming events. The API has the authoritative
	//      assistant_message; supersede the cache so a hard refresh self-heals
	//      whatever the live session rendered.
	useEffect(() => {
		if (!sessionData?.conversationId || !sessionData?.messages?.length) return;
		const lastMsg = sessionData.messages[sessionData.messages.length - 1];
		const lastWasPartial = lastMsg.role === ROLE_ASSISTANT && !lastMsg.id;

		refetchConversation(sessionData.conversationId).then(() => {
			// Partial-tail cleanup: only relevant when the cache had an unfinished
			// assistant bubble and the refetch didn't replace it (silent failure
			// or message never persisted server-side). Skipping this when the
			// cache was already complete avoids dropping a freshly-applied refetch.
			if (!lastWasPartial) return;
			setMessages((prev) => {
				const last = prev[prev.length - 1];
				if (last?.role === ROLE_ASSISTANT && !last.id) {
					return prev.slice(0, -1);
				}
				return prev;
			});
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps -- runs once on mount with initial session data
	}, []);

	const sendMessage = useCallback(
		(content: string, perMessageExtraContext?: Record<string, string>) => {
			const currentMessageCount = messages.length;

			track(AnalyticsEvent.Bizy.ChatMessageSent, {
				messageLength: content.length,
				conversationLength: currentMessageCount,
				isFirstMessage: currentMessageCount === 0,
				path: window.location.pathname,
			});

			setMessages((prev) => [...prev, { role: ROLE_USER, content, key: nextMessageKey(), timestamp: new Date() }]);
			setIsLoading(true);
			setIsTimedOut(false);
			setIsOtherTabLoading(false);
			activeRequestIdRef.current = nextPendingRequestId();
			isLoadingRef.current = true;
			streamingContentRef.current = '';
			resetStreamingAccumulators();
			messageSentAtRef.current = Date.now();
			lastUserMessageRef.current = content;
			requestGenerationRef.current += 1;
			const generation = requestGenerationRef.current;

			resetStreamTimeout();

			// `perMessageExtraContext` overrides the hook-level extraContext
			// for this single send — used by the UI block dispatcher to
			// attach per-action signals (e.g. `bizy_action: 'commit_draft'`)
			// without leaking into subsequent unrelated messages.
			const context: Record<string, string> = {
				page_path: window.location.pathname,
				...reportContext,
				...(extraContext || {}),
				...(perMessageExtraContext || {}),
			};

			mutate(
				{ message: content, conversation_id: conversationId, context },
				{
					onSuccess: (data) => {
						if (requestGenerationRef.current !== generation) return;
						if (data.conversation_id) {
							setConversationId(data.conversation_id);
						}
						if (data.request_id) {
							activeRequestIdRef.current = data.request_id;
						}
					},
					onError: (error: any) => {
						if (requestGenerationRef.current !== generation) return;
						clearStreamTimeout();

						const isBusy = error?.response?.status === 409;

						track(AnalyticsEvent.Bizy.ResponseReceived, {
							success: false,
							errorType: isBusy ? 'conversation_busy' : error instanceof Error ? error.name : 'unknown',
							path: window.location.pathname,
						});

						if (isBusy) {
							// Don't show an error — set other-tab loading state instead.
							// Remove the user message we optimistically added (it wasn't accepted).
							setMessages((prev) => {
								const last = prev[prev.length - 1];
								if (last?.role === ROLE_USER) return prev.slice(0, -1);
								return prev;
							});
							setIsOtherTabLoading(true);
							setIsLoading(false);
							isLoadingRef.current = false;
						} else {
							setMessages((prev) => [...prev, { role: ROLE_ASSISTANT, content: t('genericError'), key: nextMessageKey() }]);
							setBizyEmotion(EMOTION_DISAPPOINTED);
							setIsLoading(false);
							isLoadingRef.current = false;
						}

						setActiveToolCalls([]);
						streamingContentRef.current = '';
						messageSentAtRef.current = null;
					},
				}
			);
		},
		[mutate, conversationId, messages.length, reportContext, extraContext, resetStreamTimeout, clearStreamTimeout, t]
	);

	const retryLastMessage = useCallback(() => {
		if (!lastUserMessageRef.current) return;
		setMessages((prev) => {
			const last = prev[prev.length - 1];
			if (last?.role === ROLE_ASSISTANT && !last.id) return prev.slice(0, -1);
			return prev;
		});
		setIsTimedOut(false);
		setIsOtherTabLoading(false);
		activeRequestIdRef.current = nextPendingRequestId();
		setIsLoading(true);
		isLoadingRef.current = true;
		streamingContentRef.current = '';
		// Clear any nodes accumulated from the prior (timed-out) attempt so
		// they don't get merged onto the retry's response.
		resetStreamingAccumulators();
		messageSentAtRef.current = Date.now();
		requestGenerationRef.current += 1;
		const generation = requestGenerationRef.current;

		resetStreamTimeout();

		const context: Record<string, string> = {
			page_path: window.location.pathname,
			...reportContext,
			...(extraContext || {}),
		};

		mutate(
			{ message: lastUserMessageRef.current, conversation_id: conversationId, context },
			{
				onSuccess: (data) => {
					if (requestGenerationRef.current !== generation) return;
					if (data.conversation_id) setConversationId(data.conversation_id);
					if (data.request_id) activeRequestIdRef.current = data.request_id;
				},
				onError: () => {
					if (requestGenerationRef.current !== generation) return;
					clearStreamTimeout();
					setMessages((prev) => [...prev, { role: ROLE_ASSISTANT, content: t('genericError'), key: nextMessageKey() }]);
					setBizyEmotion(EMOTION_DISAPPOINTED);
					setIsLoading(false);
					isLoadingRef.current = false;
					setIsTimedOut(false);
					setActiveToolCalls([]);
					streamingContentRef.current = '';
					messageSentAtRef.current = null;
				},
			}
		);
	}, [mutate, conversationId, reportContext, extraContext, resetStreamTimeout, clearStreamTimeout, t]);

	const cancelGeneration = useCallback((): string | undefined => {
		if (!isLoadingRef.current) return undefined;

		requestGenerationRef.current += 1;
		const currentMessages = messagesRef.current;
		let restoredText: string | undefined;

		setMessages((prev) => {
			const last = prev[prev.length - 1];
			if (!last) return prev;

			if (last.role === ROLE_ASSISTANT && !last.id) {
				const secondLast = prev[prev.length - 2];
				if (secondLast?.role === ROLE_USER) {
					restoredText = secondLast.content;
					return prev.slice(0, -2);
				}
				return prev.slice(0, -1);
			}

			if (last.role === ROLE_USER) {
				restoredText = last.content;
				return prev.slice(0, -1);
			}

			return prev;
		});

		if (!restoredText) {
			const lastUser = [...currentMessages].reverse().find((m) => m.role === ROLE_USER);
			if (lastUser) restoredText = lastUser.content;
		}

		clearStreamTimeout();
		setIsLoading(false);
		isLoadingRef.current = false;
		setActiveToolCalls([]);
		streamingContentRef.current = '';
		// Clear here (not just in handleCancelled) because isLoadingRef is set
		// to false above, and handleCancelled's early-return guard checks that
		// flag — so if the cancelled event arrives after this line, the guard
		// fires and handleCancelled never clears the ref.
		resetStreamingAccumulators();
		messageSentAtRef.current = null;

		if (activeRequestIdRef.current) {
			cancelMutate({ request_id: activeRequestIdRef.current, conversation_id: conversationIdRef.current });
		}

		track(AnalyticsEvent.Bizy.GenerationCancelled, {
			path: window.location.pathname,
		});

		return restoredText;
	}, [clearStreamTimeout, cancelMutate]);

	const resetChat = useCallback(() => {
		clearStreamTimeout();
		activeRequestIdRef.current = undefined;
		requestGenerationRef.current += 1;
		resetStreamingAccumulators();

		setLoadConversationId(undefined);
		setMessages([]);
		setConversationId(undefined);
		setBizyEmotion(EMOTION_CURIOUS);
		setIsLoading(false);
		setIsTimedOut(false);
		setIsOtherTabLoading(false);
		isLoadingRef.current = false;
		setActiveToolCalls([]);
		streamingContentRef.current = '';
		clearBizySession();
	}, [clearStreamTimeout]);

	const updateMessageFeedback = useCallback((messageId: string, feedbackType: FeedbackType) => {
		setMessages((prev) => prev.map((msg) => (msg.id === messageId ? { ...msg, feedbackType } : msg)));
	}, []);

	const updateNodeChoice = useCallback((messageKey: string, cardIndex: number, choice: ApprovalDecision) => {
		setMessages((prev) =>
			prev.map((msg) => {
				if (msg.key !== messageKey) return msg;
				return { ...msg, nodeChoices: { ...(msg.nodeChoices ?? {}), [cardIndex]: choice } };
			})
		);
	}, []);

	const loadConversation = useCallback((id: string) => {
		setLoadConversationId(id);
	}, []);

	// Keep true until effect clears loadConversationId after applying data; avoids one-frame flash of previous messages
	const isLoadingConversation = Boolean(loadConversationId);

	return {
		messages,
		conversationId,
		isLoading,
		isTimedOut,
		isOtherTabLoading,
		isLoadingConversation,
		bizyEmotion,
		activeToolCalls,
		sendMessage,
		cancelGeneration,
		resetChat,
		retryLastMessage,
		updateMessageFeedback,
		updateNodeChoice,
		loadConversation,
	};
}
